import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import type { IInvoiceLineItemsProps } from '@/@types/invoice';

interface IInvoiceLineItemsExtendedProps extends IInvoiceLineItemsProps {
  subtotal?: number;
  tax?: number;
  total?: number;
}

export function InvoiceLineItems({
  lineItems,
  currency,
  editable = false,
  onRemove,
  subtotal,
  tax,
  total,
}: IInvoiceLineItemsExtendedProps) {
  const calcSubtotal = subtotal ?? lineItems.reduce((sum, item) => sum + item.amount, 0);
  const calcTax = tax ?? 0;
  const calcTotal = total ?? calcSubtotal + calcTax;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60%]">Description</TableHead>
          <TableHead className="text-right">Amount ({currency})</TableHead>
          {editable && <TableHead className="w-12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {lineItems.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={editable ? 3 : 2}
              className="text-center text-muted-foreground py-8"
            >
              No line items added yet.
            </TableCell>
          </TableRow>
        )}
        {lineItems.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="text-sm">{item.description}</TableCell>
            <TableCell className="text-sm text-right font-mono">
              {item.amount.toFixed(2)}
            </TableCell>
            {editable && onRemove && (
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onRemove(index)}
                  aria-label={`Remove ${item.description}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell className="text-sm font-medium">Subtotal</TableCell>
          <TableCell
            className="text-sm text-right font-mono"
            colSpan={editable ? 2 : 1}
          >
            {calcSubtotal.toFixed(2)}
          </TableCell>
        </TableRow>
        {calcTax > 0 && (
          <TableRow>
            <TableCell className="text-sm font-medium">Tax</TableCell>
            <TableCell
              className="text-sm text-right font-mono"
              colSpan={editable ? 2 : 1}
            >
              {calcTax.toFixed(2)}
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell className="text-sm font-bold">Total</TableCell>
          <TableCell
            className="text-sm text-right font-mono font-bold"
            colSpan={editable ? 2 : 1}
          >
            {currency} {calcTotal.toFixed(2)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}
